import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { qk } from '@/api/queries';
import { paths } from '@/app/paths';
import { Button, Card, Input } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { Logo } from '@/shell/Logo';
import { signIn } from './api';
import s from './auth.module.css';

/** Where to go after signing in: the page that sent the visitor here, or the dashboard. */
export function returnTo(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('/login') ? from : paths.dashboard();
}

/** /login: POST /admin/auth/login; an Admin continues on /login/mfa (verify, or enrol first). */
export function LoginPage() {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const res = await signIn(email, password);
      const next = returnTo(location.state);
      if (res.mfa_required) {
        navigate(paths.mfa(), { state: { from: next } });
        return;
      }
      qc.removeQueries();
      if (res.user) qc.setQueryData(qk.me, res.user);
      navigate(res.mfa_setup_required ? paths.mfa() : next, { replace: true, state: { from: next } });
    } catch (err) {
      setProblem(errorText(err, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.page}>
      <div className={s.stack}>
        <Logo />
        <Card padding={24} texture="dotsGlow" className={s.card}>
          <div className={s.titles}>
            <h1 className={s.title}>{t('login.title')}</h1>
            <span className={s.sub}>{t('login.sub')}</span>
          </div>
          <form className={s.form} onSubmit={(e) => void submit(e)}>
            <Input
              label={t('login.email')}
              type="email"
              autoComplete="username"
              required
              placeholder={t('login.email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label={t('login.password')}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {problem && (
              <span className={s.error} role="alert">
                {problem}
              </span>
            )}
            <Button type="submit" size={46} block spread trailingIcon="arrow-right" disabled={busy}>
              {t('login.submit')}
            </Button>
          </form>
        </Card>
        <span className={s.note}>{t('login.note')}</span>
      </div>
    </div>
  );
}
